import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useHistory } from 'react-router-dom'

import { Button, IconButton } from '@mui/material'
import Grid from '@mui/material/Grid2'
import type {
  GridCellParams,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid-premium'
import { DeleteIcon, EditIcon } from '@rfh-core/icons'
import theme from '@rfh-core/theme'

import {
  ADD_PATH,
  CustomDataGrid,
  ELEMENT_HEIGHT,
  getFileNameFromPageTitle,
  getToolbarProps,
  rowsPerPageOptions,
  ThemeConfig,
} from 'src/common'
import ButtonsBlock from 'src/common/components/ButtonsBlock'
import { IInkoperView } from 'src/common/services/client'
import { useUserStore } from 'src/common/stores/UserStore'
import { usePurchaserLimitsStore } from 'src/features/purchaserLimits'
import i18n from 'src/i18n/index'

import { purchasersColumns, ROOT_PATH } from '../constants/constants'
import { calcMarginRight } from '../lib'
import { useBuyerStore } from '../stores'
import { DeletePurchaserDialog } from './DeletePurchaserDialog'

type PurchasersGridProps = {
  isLoading: boolean
  refresh: () => void
  rows: IInkoperView[]
  totalCount: number
}

export default function PurchasersGrid({
  isLoading,
  refresh,
  rows,
  totalCount,
}: Readonly<PurchasersGridProps>): JSX.Element {
  const { t } = useTranslation()
  const { user } = useUserStore()
  const { resetPurchaserLimitsState } = usePurchaserLimitsStore()
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const [idToBeDeleted, setIdToBeDeleted] = useState<number>(0)
  const history = useHistory()
  const getRowId = useCallback((row: IInkoperView) => row.inkoperID, [])
  const mr = calcMarginRight(i18n.language)
  const { resetBuyerState } = useBuyerStore()

  const fileName = useMemo(
    (): string =>
      getFileNameFromPageTitle(t('common.titles.purchasersReadOnly')),
    [t]
  )

  const openDeleteDialog = (value: IInkoperView) => {
    setOpenDeleteModal(true)
    setIdToBeDeleted(value.inkoperID)
  }

  const closeDeleteDialog = useCallback(() => {
    setOpenDeleteModal(false)
    setIdToBeDeleted(0)
  }, [])

  const gotoPurchaserDetails = useCallback(
    (params: GridCellParams<any, IInkoperView, any>) => {
      if (params?.row?.inkoperID === undefined) {
        return
      }

      // Navigate to the details unless the checkbox cell, delete or edit button is clicked
      if (
        params?.field !== 'bewerken' &&
        params?.field !== 'delete' &&
        params?.field !== '__check__' &&
        (user.isContractBeheerder || user.isApplicationManager)
      ) {
        history.push(`/dashboard/purchasers/${params.row.inkoperID}`)
      }
    },
    [history, user.isApplicationManager, user.isContractBeheerder]
  )

  useEffect(() => {
    resetPurchaserLimitsState()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const getColumns = useCallback((): GridColDef[] => {
    const staticColumns = [...purchasersColumns]
    if (user.isContractBeheerder || user.isApplicationManager) {
      staticColumns.push({
        field: 'bewerken',
        type: 'actions',
        headerName: t('common.edit'),
        headerAlign: 'center',
        align: 'center',
        disableColumnMenu: true,
        hideable: false,
        sortable: false,
        disableExport: true,
        renderHeader: () => (
          <EditIcon
            sx={{
              color: `${theme.rfhColors.white}`,
              width: '20px',
              height: '20px',
            }}
          />
        ),
        renderCell: (params: GridRenderCellParams<any, IInkoperView>) =>
          !params.row.isVervallen ? (
            <IconButton
              component={Link}
              to={`/dashboard/purchasers/${params.row.inkoperID}`}
            >
              <EditIcon
                data-testid='edit'
                sx={{
                  cursor: 'pointer',
                  color: `${theme.rfhColors.grey[800]}`,
                  width: '20px',
                  height: '20px',
                }}
              />
            </IconButton>
          ) : (
            <EditIcon
              data-testid='edit'
              sx={{
                color: `${theme.rfhColors.grey[200]}`,
                width: '20px',
                height: '20px',
              }}
            />
          ),
        flex: 0.5,
      })
      staticColumns.push({
        field: 'delete',
        type: 'actions',
        headerName: t('common.delete'),
        headerAlign: 'center',
        align: 'center',
        disableColumnMenu: true,
        hideable: false,
        sortable: false,
        disableExport: true,
        renderHeader: () => (
          <DeleteIcon
            sx={{
              color: theme.rfhColors.white,
              width: '20px',
              height: '20px',
            }}
          />
        ),
        renderCell: (params: GridRenderCellParams): JSX.Element =>
          !params.row.isVervallen ? (
            <IconButton onClick={(): void => openDeleteDialog(params.row)}>
              <DeleteIcon
                data-testid='delete'
                sx={{
                  cursor: 'pointer',
                  color: `${theme.rfhColors.grey[800]}`,
                  width: '20px',
                  height: '20px',
                }}
              />
            </IconButton>
          ) : (
            <DeleteIcon
              data-testid='delete'
              sx={{
                color: `${theme.rfhColors.grey[200]}`,
                width: '20px',
                height: '20px',
              }}
            />
          ),
        flex: 0.5,
      })
    }

    return staticColumns
  }, [t, user.isApplicationManager, user.isContractBeheerder])

  return (
    <>
      {user.isApplicationManager || user.isContractBeheerder ? (
        <Grid container flexDirection={'row'} justifyContent={'end'}>
          <ButtonsBlock gap={ThemeConfig.spacing.s} mr={mr}>
            <Button
              onClick={resetBuyerState}
              variant='contained'
              component={Link}
              to={`/dashboard/${ROOT_PATH}/${ADD_PATH}`}
              sx={{ height: ELEMENT_HEIGHT }}
            >
              {t('common.add')}
            </Button>
          </ButtonsBlock>
        </Grid>
      ) : null}
      <CustomDataGrid
        columns={getColumns()}
        getRowId={getRowId}
        gridName={'purchasers'}
        gridProps={{
          export: {
            title: t('purchaser.purchasers'),
          },
          loading: isLoading,
          rowCount: totalCount,
          onCellClick: gotoPurchaserDetails,
          pageSizeOptions: [...rowsPerPageOptions, 250, 500, 1000],
        }}
        language={i18n.language}
        rows={rows}
        toolbarProps={getToolbarProps(fileName)}
      />
      <DeletePurchaserDialog
        idToBeDeleted={idToBeDeleted}
        onCancel={closeDeleteDialog}
        onClose={closeDeleteDialog}
        open={openDeleteModal}
        onRefresh={refresh}
      />
    </>
  )
}
